import React, { useMemo } from "react";
import { Box, Stack, DialogActions, DialogContent, Typography, FormControl, RadioGroup, FormControlLabel, Radio, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { HtmlTooltip, StyledDarkRedInformationTypography, StyledMainBox } from "../../../../components/commonComponents/StyledComponents/styled";
import { Close, ExpandCircleDown, Save } from "@mui/icons-material";
import CloseButton from "../../../../components/commonComponents/Buttons/CloseButton";
import { useDispatch, useSelector } from "react-redux";
import ListingCheckUi from "../../ListingCheck/ListingCheckUi";
import ActionButton from "../../../../components/commonComponents/Buttons/ActionButton";
import { updateStateData } from "../../../../Store/actions";
import { fetchSavedListingCheckData, saveListingCheckResults } from "../../../../api_calls/listingCheck";
import { toast } from "react-toastify";
import { DisabledGreyButton } from "../../../../components/commonComponents/Buttons/DisabledButton";
import Results from "../../ListingCheck/Results";
import CommonConfirmation from "../../../../components/commonComponents/CommonConfirmation";

// New component to render ListingCheckUi and save options (used in both with/without saved listing check scenarios)
const NewListingCheckUi = ({ usedFor, adlevelGroupOptions, showAnyGtinSaveButton, showAllGtinSaveButton, dispatchAction, campaignRecord, currentStateData, saveListingCheckResults, setExpanded }) => {
    return (
        <>
            <ListingCheckUi usedFor={usedFor} analysisGroupOptions={adlevelGroupOptions} campaignRecord={campaignRecord} />
            {/* --------- Save listing check options (Only shown if the listing check returned at least one GTIN being listed in at least one store) ----------- */}
            {
                (showAnyGtinSaveButton || showAllGtinSaveButton) && (
                    <>
                        <StyledDarkRedInformationTypography variant="body2" sx={{ fontSize: "18px", top: 0 }}>
                            Save Listing Check for Campaign
                        </StyledDarkRedInformationTypography>
                        <Box height={10} />
                        <Typography variant="body2">
                            You can save the Listing Check results for this campaign to add relevant stores more easily to contact-based visuals later. You can save a new Listing Check at any time. Saving a Listing Check does not affect visuals that have already been created.
                        </Typography>
                        <Box height={10} />
                        <FormControl>
                            <RadioGroup
                                aria-labelledby="listing-check-save-options"
                                name="listing-check-save-options-group"
                            >
                                <Stack direction={"row"} spacing={4} >
                                    {
                                        showAllGtinSaveButton === true ? (
                                            <FormControlLabel
                                                value="AllGtins"

                                                control={<Radio sx={{
                                                    color: "#AF3241",
                                                    paddingLeft: "0px",
                                                    "&.Mui-checked": {
                                                        color: "#AF3241"
                                                    }
                                                }} />}
                                                label="Stores with all GTINs listed"
                                                onChange={(e) => {
                                                    dispatchAction(updateStateData({
                                                        listing_check_save_option: e.target.value
                                                    }))
                                                }}
                                            />
                                        ) : (
                                            <HtmlTooltip title="No stores found with all GTINs listed">
                                                <FormControlLabel
                                                    sx={{ ml: 0 }}
                                                    value="AllGtins"
                                                    disabled
                                                    control={<Radio disableRipple />}
                                                    label="Stores with all GTINs listed"
                                                />
                                            </HtmlTooltip>
                                        )
                                    }

                                    {
                                        showAnyGtinSaveButton === true ? (
                                            <FormControlLabel
                                                value="AnyGtins"
                                                control={<Radio sx={{
                                                    color: "#AF3241",
                                                    "&.Mui-checked": {
                                                        color: "#AF3241"
                                                    }
                                                }} />}
                                                label="Stores with any GTIN listed"
                                                onChange={(e) => {
                                                    dispatchAction(updateStateData({
                                                        listing_check_save_option: e.target.value
                                                    }))
                                                }}
                                            />
                                        ) : (
                                            <HtmlTooltip title="No stores found with any GTINs listed">
                                                <FormControlLabel
                                                    sx={{ ml: 0 }}
                                                    value="AllGtins"
                                                    disabled
                                                    control={<Radio disableRipple />}
                                                    label="Stores with any GTIN listed"
                                                />
                                            </HtmlTooltip>
                                        )
                                    }
                                </Stack>

                            </RadioGroup>
                        </FormControl>
                        {/* ---------- save button -------- */}
                        <Box width={450}>
                            {
                                (showAnyGtinSaveButton || showAllGtinSaveButton) && typeof currentStateData.listing_check_save_option !== "undefined" ? (
                                    <ActionButton
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (typeof currentStateData.listing_check_save_option === "undefined" || currentStateData.listing_check_save_option === null) {
                                                toast("Please select a save option before saving the Listing Check results.", { type: "error" });
                                            } else {
                                                saveListingCheckResults(campaignRecord.campaign_id).then(() => {
                                                    setExpanded(false);
                                                })
                                            }
                                        }}
                                        label={"Save Listing Check"}
                                        icon={<Save />}
                                    />
                                ) : (
                                    <DisabledGreyButton
                                        onClick={(e) => {
                                            e.preventDefault();
                                        }}
                                        label={"Save Listing Check"}
                                        icon={<Save />}
                                        sx={{ mt: 3, mb: 2 }}
                                    />
                                )
                            }
                        </Box>
                    </>
                )
            }

        </>
    )
}

// Main ListingCheck View component used in add product Modal
const ListingCheckView = ({ closeModal, campaignRecord }) => {
    const currentStateData = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    const [expanded, setExpanded] = React.useState(false);
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const listingRecords = typeof currentStateData.created_adlevels !== "undefined" ? currentStateData.created_adlevels : [];
    // get listing check response data
    const listingCheckResponseData = typeof currentStateData.listing_check_response_data !== "undefined" ? currentStateData.listing_check_response_data : {};
    // determine whether to show save buttons
    const showAnyGtinSaveButton = typeof listingCheckResponseData.anyGtinCount !== "undefined" && listingCheckResponseData.anyGtinCount > 0;
    const showAllGtinSaveButton = typeof listingCheckResponseData.allGtinCount !== "undefined" && listingCheckResponseData.allGtinCount > 0

    // function to fetch products based on adlevel number
    const fetchProducts = (numberAdlevel) => {
        //fetch products based on adlevel
        return currentStateData.created_adlevels.filter(prod => {
            return prod.numberAdLevel === numberAdlevel;
        }).map(prod => {
            return {
                label: prod.description,
                value: prod.gtin,
            }
        });
    }

    // to generate options for hierarchical adlevel groups (usememo for performance optimization, it will only recalculate when listingRecords change)
    const adlevelGroupOptions = useMemo(() => {
        let uniqueAdlevels = [];
        // find unique adlevel number and names
        listingRecords.forEach(groupDtl => {
            if (!uniqueAdlevels.some(adlevel => adlevel.numberAdLevel === groupDtl.numberAdLevel)) {
                uniqueAdlevels.push({ numberAdLevel: groupDtl.numberAdLevel, nameAdLevel: groupDtl.nameAdLevel });
            }
        });
        // fetch products for each unique adlevel
        const createOptions = uniqueAdlevels.map((adlevel) => {
            const options = fetchProducts(adlevel.numberAdLevel);
            if (options.length === 0) return null;
            return {
                category_name: adlevel.numberAdLevel + " - " + adlevel.nameAdLevel,
                options: options,
            }
        });

        return createOptions.filter(record => record !== null);
    }, [listingRecords]);

    React.useEffect(() => {
        fetchSavedListingCheckData(campaignRecord.campaign_id);
    }, []);

    return (
        <>
            <Box height={15} />
            {/*
                     --------- load saved listing check (if any) --------- 
                     ------- Listing check related fields and results -------- 
                     */}
            {
                typeof currentStateData.listing_check_saved_data !== "undefined" ? (
                    <>
                        {/* --------- saved listing check results ----------- */}
                        <Results
                            reduxKeyToLoadResults={"listing_check_saved_data"}
                            reduxKeyToCheckSortedProducts={"sorted_saved_product_list_data"}
                            resultsTitle={"Saved Listing Check"}
                            campaignRecord={campaignRecord} //to indicate that results are being shown in products modal
                            usedFor="savedListingCheck"
                        />
                        {/* ---------- to show a new listing check ui -------- */}
                        <Accordion sx={{ mt: 2, mb: 2, "&.MuiAccordion-root": { boxShadow: "none" } }} expanded={expanded} onChange={() => setExpanded(!expanded)}>
                            <AccordionSummary
                                expandIcon={<ExpandCircleDown sx={{ color: "#AF3241" }} />}
                                aria-controls="panel2-content"
                                sx={{
                                    "&.MuiAccordionSummary-root": {
                                        padding: 0
                                    }
                                }}
                            >
                                <StyledDarkRedInformationTypography variant="body2" sx={{ fontSize: "18px", fontWeight: 500 }}>
                                    New Listing Check
                                </StyledDarkRedInformationTypography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ "&.MuiAccordionDetails-root": { padding: 0 } }}>
                                <NewListingCheckUi
                                    usedFor="withSavedListingCheck"
                                    adlevelGroupOptions={adlevelGroupOptions}
                                    showAnyGtinSaveButton={showAnyGtinSaveButton}
                                    showAllGtinSaveButton={showAllGtinSaveButton}
                                    dispatchAction={dispatchAction}
                                    campaignRecord={campaignRecord}
                                    currentStateData={currentStateData}
                                    saveListingCheckResults={saveListingCheckResults}
                                    setExpanded={setExpanded}
                                />
                            </AccordionDetails>
                        </Accordion>
                    </>
                ) : (
                    <NewListingCheckUi usedFor="withoutSavedListingCheck"
                        adlevelGroupOptions={adlevelGroupOptions}
                        showAnyGtinSaveButton={showAnyGtinSaveButton}
                        showAllGtinSaveButton={showAllGtinSaveButton}
                        dispatchAction={dispatchAction}
                        campaignRecord={campaignRecord}
                        currentStateData={currentStateData}
                        saveListingCheckResults={saveListingCheckResults}
                        setExpanded={setExpanded}
                    />
                )
            }
            <Box height={20} />
            <DialogActions>
                <Stack direction={"row"} spacing={2}>
                    {/* ----------- cancel/close --------- */}
                    <CloseButton
                        onClick={(e) => {
                            e.preventDefault();
                            if (typeof currentStateData.listing_check_generated !== "undefined" && currentStateData.listing_check_generated === true) {
                                setShowConfirmation(true);
                            } else {
                                closeModal();
                            }
                        }}
                        label="Close"
                        icon={<Close />}
                    />

                </Stack>
            </DialogActions>
            {showConfirmation === true && (
                <CommonConfirmation
                    confirmationActionCall={() => {
                        closeModal();
                    }}
                    closePopup={(e) => {
                        setShowConfirmation(false);
                    }}
                    confirmTitle="Unsaved Changes"
                    showConfirmationPopup={showConfirmation}
                    confirmationMessage="You have unsaved changes which will be lost. Are you sure you want to cancel?"
                />
            )}
        </>
    );
};

export default ListingCheckView;
